import { useRelativeDrag } from 'lib/events-framework/useRelativeDrag'
import React, { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { Slide } from 'ui/slider/Slide'
import { State } from 'ui/slider/useSliderTranslation'

import { sliderContext } from '.'

type Props = {
  slidesPerPage: number
  groupWidth: number | null
  state: State
  onShiftRequest(value: number): void
  slideWidth?: null | number
  maxTranslation: number
  paddingForOverflow?: string | number
  isSwipingDisabled: boolean
  children: ReactNode
}

export const SliderContent = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      state,
      groupWidth,
      onShiftRequest,
      slidesPerPage,
      slideWidth,
      maxTranslation,
      paddingForOverflow,
      isSwipingDisabled
    },
    ref
  ) => {
    const [initialTranslation, setInitialTranslation] = useState<null | number>(null)
    const [translation, setTranslation] = useState<null | number>(null)
    const [touchTimeStart, setTouchTimeStart] = useState<null | number>(null)
    const [isTransitionActive, setIsTransitionActive] = useState(false)
    const isFirstLoad = useRef(true)
    // Нельзя использовать picked, т.к. picked становится true при pointerDown, а нам нужно отслеживать, что курсором было пройдено расстояние
    const { setIsMoving, isMoving } = useContext(sliderContext)

    const swipeMove = useCallback(
      (initialTranslation: number, distance: number) => {
        setIsMoving(true)
        // Из количества слайдов необходимо вычесть количество слойдов последней группы, т.к. transition слайдера считается до первого слайда в зоне видимости
        if (initialTranslation + distance > 0) {
          /* Когда пользователь двигает слайдер за его пределы, скорость с которой изменяется transition становится все меньше и меньше,
         это происходит благодаря возведению пройденного расстояния в степень 0.85
         Также надо из пройденного расстояния вычитать разницу между transition на момент начала движения и крайними координатами, иначе
         когда условие сработает, добавочный transition пересчитается и слайдер "прыгнет"
       */
          setTranslation(Math.sign(distance) * Math.abs(distance + initialTranslation) ** 0.85)
        } else if (initialTranslation + distance < maxTranslation) {
          setTranslation(
            maxTranslation + Math.sign(distance) * Math.abs(distance - maxTranslation + initialTranslation) ** 0.85
          )
        } else {
          setTranslation(initialTranslation + distance)
        }
      },
      [maxTranslation]
    )

    const shiftSlider = useCallback(
      ([distanceDiff]: number[]) => {
        if (groupWidth !== null && touchTimeStart !== null) {
          const timeDiff = Date.now() - touchTimeStart
          let ratioToSlide: number = 0
          if (timeDiff > 300) {
            if (distanceDiff > 0) {
              ratioToSlide = 0.25
            } else {
              ratioToSlide = 0.75
            }
          } else {
            if (distanceDiff > 0) {
              ratioToSlide = 0.03
            } else {
              ratioToSlide = 0.97
            }
          }
          onShiftRequest(Math.floor((groupWidth * ratioToSlide - distanceDiff) / groupWidth))
        }
      },
      [groupWidth, onShiftRequest, touchTimeStart]
    )

    const swipeSlider = useCallback(
      ([x, y]: number[]) => {
        if (initialTranslation !== null) {
          swipeMove(initialTranslation, x)
        }
      },
      [initialTranslation, swipeMove]
    )

    //Мы не можем использовать поинтеры, т.к. отменить скролл можно только с помощью touchEvents
    const { pick } = useRelativeDrag({
      position: [0, 0],
      onPositionChange: swipeSlider,
      onDrop: shiftSlider,
      interactiveDirection: 'horizontal'
    })

    useEffect(() => {
      setTranslation(state.translation)
      if (!state.disableTransition) {
        setIsTransitionActive(true)
      }
    }, [state])

    useEffect(() => {
      if (ref !== null && typeof ref === 'object' && ref.current) {
        ref.current.addEventListener('touchmove', () => {}, false)
      }
    }, [])

    useEffect(() => {
      const handler = () => setIsMoving(false)
      if (isMoving) document.addEventListener('pointerup', handler)

      return () => document.removeEventListener('pointerup', handler)
    }, [isMoving])

    const Slides = useMemo(() => {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === Slide) {
          return React.cloneElement(child, { slidesPerPage, slideWidth })
        } else if (child !== null) {
          console.error('children should be of type `Slide or null`')
        }

        return null
      })
    }, [children, slidesPerPage, slideWidth])

    return (
      <>
        <StyledSliderContent
          ref={ref}
          onTransitionEnd={(e) => {
            if (ref !== null && typeof ref === 'object' && ref.current && e.target === ref.current) {
              setIsTransitionActive(false)
            }
          }}
          isTransitionActive={isTransitionActive}
          style={{ transform: `translateX(${translation}px)` }}
          onPointerDown={(e) => {
            if (isSwipingDisabled) return
            setIsTransitionActive(false)
            setInitialTranslation(translation)
            setTouchTimeStart(Date.now())
            pick(e)
          }}
          onDragStart={(e) => e.preventDefault()}
          paddingForOverflow={paddingForOverflow}
        >
          {Slides}
        </StyledSliderContent>
      </>
    )
  }
)

const StyledSliderContent = styled.div<{
  isTransitionActive: boolean
  paddingForOverflow?: string | number
}>`
  display: flex;
  height: 100%;

  transition: ${({ isTransitionActive }) => (isTransitionActive ? 'transform 0.45s' : 'none')};
  padding: ${({ paddingForOverflow }) =>
    paddingForOverflow !== undefined
      ? `0 ${paddingForOverflow}${typeof paddingForOverflow === 'string' ? '' : 'rem'}`
      : 0};
`
